import { createContext, useCallback, useMemo, useState } from "react";
import type { LabelValue } from "types";

export interface ListOptionsType {
  option: LabelValue | undefined;
}

export const ListOptionsContext = createContext<ListOptionsType | undefined>(undefined);
ListOptionsContext.displayName = "ListOptionsContext";

export interface ListOptionsActionsType {
  onSetSelectedOption: (option: LabelValue | undefined) => void;
  onSetToInitialOption: () => void;
}

export const ListOptionsActionsContext = createContext<ListOptionsActionsType | undefined>(undefined);
ListOptionsActionsContext.displayName = "ListOptionsActionsContext";

export interface ListOptionsProviderProps {
  initialOption?: LabelValue;
  /**
   * If true, only one option can be selected at a time (default is false).
   * Clicking on an already selected option will deselect it.
   */
  exclusive?: boolean;
  children?: React.ReactNode;
}

function ListOptionsProvider(props: ListOptionsProviderProps) {
  const { exclusive = false } = props;
  const [selectedOption, setSelectedOption] = useState<LabelValue | undefined>(props.initialOption);

  const onSetSelectedOption = useCallback(
    (option: LabelValue | undefined) => {
      if (exclusive && selectedOption?.value === option?.value) {
        setSelectedOption(undefined);
        return;
      }
      setSelectedOption(option);
    },
    [exclusive, selectedOption?.value]
  );

  const onSetToInitialOption = useCallback(() => {
    setSelectedOption(props.initialOption);
  }, [props.initialOption, setSelectedOption]);

  const actions: ListOptionsActionsType = useMemo(
    () => ({ onSetSelectedOption, onSetToInitialOption }),
    [onSetSelectedOption, onSetToInitialOption]
  );
  const value: ListOptionsType = useMemo(() => ({ option: selectedOption }), [selectedOption]);

  return (
    <ListOptionsContext.Provider value={value}>
      <ListOptionsActionsContext.Provider value={actions}>{props.children}</ListOptionsActionsContext.Provider>
    </ListOptionsContext.Provider>
  );
}

export default ListOptionsProvider;
